import { lazyMarkReadOnTableViewChange } from "owa-mail-mark-read-actions";
import type { TableView, MailFolderTableQuery } from "owa-mail-list-store";
import { type TableQueryType, getViewFilterForTable, isUnreadSearchFolder, isCopilotResultsTabVisible, isPriorityView, setCopilotResultsTableViewIdsMutator } from "owa-mail-list-store";
import type { ActionSource } from "owa-mail-store";
import { lazyCleanSearchTableState } from "owa-mail-execute-search-actions";
import tombstoneOperations from "owa-mail-list-tombstone";
import { shouldShowReadingPane } from "owa-mail-layout";
import updatePrioritizeRequestStatusNudgedMutator from "owa-mail-priority-inbox-store/lib/mutators/updatePrioritizeRequestStatusNudgedMutator";
import { getUserStore, setPriorityViewEnteredTimestampMutator, clearPrioritizeRunContextMutator, } from "owa-mail-priority-inbox-store";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { logPrioritizeTelemetryEvent } from "owa-copilot-telemetry/lib/prioritizeTelemetryLogger";
import { isPrioritizeOnDeliveryOn } from "owa-copilot-settings-utils";
import { getAggregationBucket } from "owa-analytics-actions";
import { getFolderTable, getSearchFolderHasUnreadRestriction, lazyFetchSearchFolderUnreadRestrictions, } from "owa-folders";
import type SearchFolderTemplateId from "owa-service/lib/contract/SearchFolderTemplateId";

/**
 * Called after loading the new table. The reason we are passing the selectedRowKeys
 * and not reading them from the oldTableView is because the reset selection has already
 * been called in onBeforeTableLoad and it has to happen synchronously before the new
 * table loads else we end up in not resetting the LV/RP views in certain scenarios.
 * @param selectedRowKeysForOldTable the selectedRowKeys for the old tableview
 * @param oldTableView the old table view
 * @param newTableView the new tableView we are switching to
 */
export async function onAfterTableLoad(selectedRowKeysForOldTable: string[], oldTableView: TableView, newTableView: TableView, actionSource?: ActionSource): Promise<void> {
    // Clear entries from the tombstone for the old table
    if (oldTableView.tableQuery.type === 0) {
        tombstoneOperations.clearMapForTableViewMutator(oldTableView);
    }
    
    ensureSearchFolderUnreadRestrictionLoaded(newTableView);
    
    if (shouldMarkReadOnTableViewChange(selectedRowKeysForOldTable, newTableView)) {
        const markReadOnTableChange = await lazyMarkReadOnTableViewChange.import();
        markReadOnTableChange(selectedRowKeysForOldTable, oldTableView);
    }
    
    const folderName = folderIdToName(newTableView.tableQuery.folderId);
    const mailboxInfo = getMailboxInfo(newTableView);
    if (folderName === "inbox") {
        updatePrioritizeRequestStatusNudgedMutator(mailboxInfo, newTableView);
    }
    
    // Detect when the user transitions OUT of Priority View. Covers explicit Exit
    // (closePriorityView -> loadTableViewFromTableQuery), folder switching, search,
    // pivot/group navigation, etc. Does NOT cover tab/browser close.
    const wasInPriorityView = isPriorityView(oldTableView.tableQuery as MailFolderTableQuery);
    const isInPriorityView = isPriorityView(newTableView.tableQuery as MailFolderTableQuery);
    if (wasInPriorityView && !isInPriorityView) {
        // Use the OLD table view's mailbox: the PV being closed belongs to the previous
        // account, not necessarily the one we're switching to.
        const oldMailboxInfo = getMailboxInfo(oldTableView);
        const userStore = getUserStore(oldMailboxInfo);
        const enteredTimestamp = userStore.priorityViewEnteredTimestamp;
        if (enteredTimestamp !== undefined) {
            const durationSeconds = Math.round((Date.now() - enteredTimestamp) / 1000);
            logPrioritizeTelemetryEvent("PrioritizeViewClosed", oldMailboxInfo, {
                source: userStore.prioritizeSource,
                prioritizeRunId: userStore.prioritizeRunId,
                isPrioritizeOnDeliveryOn: isPrioritizeOnDeliveryOn(oldMailboxInfo),
                actionSource,
                durationSeconds: getAggregationBucket({
                    value: durationSeconds,
                    exactMatches: [0],
                    buckets: [5, 15, 30, 60, 120, 300, 600, 1800, 3600],
                }),
            });
            setPriorityViewEnteredTimestampMutator(oldMailboxInfo, undefined);
            clearPrioritizeRunContextMutator(oldMailboxInfo);
        }
    }
    
    // Keep the copilot results' origin table in sync when switching folders or applying refiners
    if (isCopilotResultsTabVisible(mailboxInfo) &&
        (newTableView.tableQuery.type === 0 ||
            (newTableView.tableQuery.type === 1 &&
                (actionSource === "SearchRefiner" || actionSource === "SearchScopeRefiner")))) {
        setCopilotResultsTableViewIdsMutator(mailboxInfo, undefined, newTableView.id);
    }
    
    // Clean the old table View's state.
    // Currently we do not have to clean up anything on a non-search (or Folder) table
    // as we are caching them and the only clean up that we perform is the resetting the selection.
    if (oldTableView.tableQuery.type === 1 &&
        oldTableView.id !== newTableView.id &&
        newTableView.tableQuery.type !== 4) {
        /**
         * The reason we want to wait on markReadOnTableChange promise is to make sure the
         * mark read operation completes correctly on the row that was selected in the search table,
         * before we try to clear the rows in the table
         */
        lazyCleanSearchTableState(oldTableView);
    }
}

/**
 * Load custom search folder restrictions on demand when the folder is opened.
 * Automatic mark-read waits for classification if this request is still pending.
 */
function ensureSearchFolderUnreadRestrictionLoaded(tableView: TableView) {
    if (tableView.tableQuery.type !== 0) {
        return;
    }
    
    const folderId = tableView.tableQuery.folderId;
    const folder = getFolderTable().get(folderId);
    if (folder?.SearchFolderTemplateId === 1 &&
        getSearchFolderHasUnreadRestriction(folderId) === undefined) {
        lazyFetchSearchFolderUnreadRestrictions
            .import()
            .then(fetchRestrictions => fetchRestrictions(folder.mailboxInfo, [folderId]))
            .catch(() => {
            // Without the restriction the folder keeps the existing (template-based) behavior
        });
    }
}

/**
 * Return true if all of these conditions are met:
 * 1) Single row was selected in the old table
 * 2) Reading pane is shown
 * 3) Not moving to unread filter
 * */
function shouldMarkReadOnTableViewChange(oldSelectedRowKeys: string[], newTableView: TableView): boolean {
    return (oldSelectedRowKeys.length == 1 &&
        shouldShowReadingPane() &&
        getViewFilterForTable(newTableView) !== "Unread" &&
        !isUnreadSearchFolder(newTableView));
}
